import React, {
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import type { TreeProps } from "antd";
import clsx from "clsx";
import type {
  FolderTreeData,
  FolderTreeProps,
  FolderTreeRef,
  EditNodeInfo,
  CreateNodeContext,
} from "./types";
import DirectoryTreeComponent from "./DirectoryTree";
import FilePreview from "./FilePreview";
import { useStyles } from "./styles";

export type {
  FolderTreeData,
  FolderTreeProps,
  FileContentService,
  FolderTreeRef,
} from "./types";

// 展开所有节点
function getAllExpandedKeys(
  nodes: FolderTreeData[],
  parentPath = "",
): string[] {
  const keys: string[] = [];

  nodes.forEach((node) => {
    const currentPath = parentPath ? `${parentPath}/${node.path}` : node.path;

    if (node.children?.length) {
      keys.push(currentPath);
      keys.push(...getAllExpandedKeys(node.children, currentPath));
    }
  });

  return keys;
}

// 格式化数据
function normalizeTreeData(nodes: FolderTreeData[]): FolderTreeData[] {
  return nodes.map((node) => ({
    ...node,
    type: node.type
      ? node.type
      : !!node.children && node.children.length > 0
        ? "folder"
        : "file",
    children: node.children ? normalizeTreeData(node.children) : undefined,
  }));
}

const FolderTree = forwardRef<FolderTreeRef, FolderTreeProps>((props, ref) => {
  const {
    className,
    style,
    treeData,
    directoryIcons,
    previewRender,
    directoryTitle,
    previewTitle,
    selectable = true,
    defaultSelectedFile,
    defaultExpandAll = true,
    selectedFile,
    onSelectedFileChange,
    directoryTreeWidth = 280,
    emptyRender,
    defaultExpandedPaths,
    expandedPaths,
    onExpandedPathsChange,
    onFileClick,
    onFolderClick,
    fileContentService,
    moreActions,
    showLine,
    switcherIcon,
    showPreview = true,
    draggable,
    onDrop,
    onRename,
    onCreate,
  } = props;

  const styles = useStyles();

  const normalizedTreeData = useMemo(
    () => normalizeTreeData(treeData),
    [treeData],
  );

  // ====== 编辑态状态 ======
  const [editNode, setEditNode] = useState<EditNodeInfo | null>(null);
  const [createContext, setCreateContext] = useState<CreateNodeContext | null>(
    null,
  );

  // ====== 工具函数 ======
  const findNodeAndValidate = useCallback(
    (
      path: string | string[],
      validateAsFile = false,
    ): { node: FolderTreeData | undefined; isValid: boolean } => {
      if (!path) return { node: undefined, isValid: false };
      const segments = Array.isArray(path)
        ? path.filter(Boolean)
        : path.split("/").filter(Boolean);
      if (segments.length === 0) return { node: undefined, isValid: false };

      const findNode = (
        nodes: FolderTreeData[],
        index = 0,
      ): FolderTreeData | undefined => {
        if (index >= segments.length) return undefined;
        const currentSegment = segments[index];
        for (const node of nodes) {
          if (node.path === currentSegment) {
            return index === segments.length - 1
              ? node
              : node.children
                ? findNode(node.children, index + 1)
                : undefined;
          }
        }
        return undefined;
      };

      const node = findNode(normalizedTreeData);
      const isValid = validateAsFile
        ? !!node && (!node?.children || node.children.length === 0)
        : !!node;
      return { node, isValid };
    },
    [normalizedTreeData],
  );

  const isValidSelectedFile = (filePath?: string[]): boolean =>
    !!(
      filePath &&
      filePath.length > 0 &&
      findNodeAndValidate(filePath, true).isValid
    );

  // ====== 编辑态逻辑 ======

  // 当 normalizedTreeData 变化时，检查编辑节点是否还存在
  useEffect(() => {
    if (!editNode) return;
    const { node } = findNodeAndValidate(editNode.key);
    if (!node) {
      setEditNode(null);
      setCreateContext(null);
    }
  }, [normalizedTreeData]);

  // 编辑值变化
  const handleEditValueChange = useCallback((value: string) => {
    setEditNode((prev) => (prev ? { ...prev, currentValue: value } : null));
  }, []);

  // 确认编辑
  const handleEditConfirm = useCallback((value?: string) => {
    if (!editNode) return;
    const newValue = (value ?? editNode.currentValue).trim();

    if (editNode.mode === "rename") {
      const { node } = findNodeAndValidate(editNode.key);
      onRename?.({
        key: editNode.key,
        oldTitle: editNode.originalTitle,
        newTitle: newValue,
        node: node!,
      });
    } else if (editNode.mode === "create" && createContext) {
      onCreate?.({
        parentKey: createContext.parentKey,
        name: newValue,
        type: createContext.type,
        extra: createContext.extra,
      });
    }

    setEditNode(null);
    setCreateContext(null);
  }, [editNode, createContext, findNodeAndValidate, onRename, onCreate]);

  // 取消编辑
  const handleEditCancel = useCallback(() => {
    setEditNode(null);
    setCreateContext(null);
  }, []);

  // 开始重命名
  const handleStartRename = useCallback(
    (fullPath: string, node: FolderTreeData) => {
      const titleStr =
        typeof node.title === "string" ? node.title : String(node.path);
      setCreateContext(null);
      setEditNode({
        key: fullPath,
        type: node.type || "file",
        originalTitle: titleStr,
        currentValue: titleStr,
        mode: "rename",
      });
    },
    [],
  );

  // ====== State ======
  const [selectedFileState, setSelectedFileState] = useState<string[]>(() =>
    isValidSelectedFile(selectedFile || defaultSelectedFile)
      ? selectedFile || defaultSelectedFile || []
      : [],
  );
  const controlled = expandedPaths !== undefined;

  const [expandedPathsState, setExpandedPathsState] = useState<string[]>(
    defaultExpandedPaths || [],
  );
  const [validSelectedFile, setValidSelectedFile] = useState<boolean>(
    isValidSelectedFile(selectedFile || defaultSelectedFile),
  );
  const [fileContent, setFileContent] = useState<string>("");
  const [loadingContent, setLoadingContent] = useState<boolean>(false);
  const initializedExpandRef = useRef(false);

  // 受控模式同步
  useEffect(() => {
    if (selectedFile !== undefined) {
      setSelectedFileState(selectedFile);
      setValidSelectedFile(isValidSelectedFile(selectedFile));
    }
  }, [selectedFile, isValidSelectedFile]);

  useEffect(() => {
    if (
      !controlled &&
      defaultExpandAll &&
      !initializedExpandRef.current &&
      normalizedTreeData.length > 0
    ) {
      initializedExpandRef.current = true;

      setExpandedPathsState(getAllExpandedKeys(normalizedTreeData));
    }
  }, [controlled, defaultExpandAll, normalizedTreeData]);

  useEffect(() => {
    if (controlled) {
      setExpandedPathsState(expandedPaths);
    }
  }, [controlled, expandedPaths]);

  // ====== 加载文件内容 ======
  useEffect(() => {
    const loadContent = async () => {
      if (!validSelectedFile || selectedFileState.length === 0) {
        setFileContent("");
        setLoadingContent(false);
        return;
      }

      const filePath = selectedFileState.join("/");
      const segments = filePath.split("/").filter((s) => s !== "");
      const { node } = findNodeAndValidate(segments);

      if (fileContentService) {
        setLoadingContent(true);
        try {
          const content = await fileContentService.loadFileContent(filePath);
          setFileContent(content);
        } catch (error) {
          setFileContent(
            `// 加载错误: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          );
        } finally {
          setLoadingContent(false);
        }
      } else if (node?.content) {
        setFileContent(node.content);
        setLoadingContent(false);
      } else {
        setFileContent("");
        setLoadingContent(false);
      }
    };

    loadContent();
  }, [
    validSelectedFile,
    selectedFileState,
    normalizedTreeData,
    fileContentService,
    findNodeAndValidate,
  ]);

  // ====== 事件处理 ======
  const handleSelect: TreeProps["onSelect"] = (_keys, info) => {
    const keys = _keys as string[];
    const nodes = Array.isArray(info.selectedNodes)
      ? info.selectedNodes
      : [info.selectedNodes];

    const isFolder = nodes.some((node) => !node.isLeaf);
    if (isFolder) {
      if (nodes.length === 1) {
        const node = nodes[0] as unknown as FolderTreeData;
        onFolderClick?.(node.path, node.extra);
      }
      return;
    }

    const pathArray = keys[0]?.split("/").filter(Boolean) || [];
    if (pathArray.length === 0) return;

    // 从原始 normalizedTreeData 中查找节点，避免拿到 antd 包装后的 JSX title
    const { node: originalNode } = findNodeAndValidate(pathArray);
    const fileName = originalNode?.title;
    const nodeContent = originalNode?.content;

    onSelectedFileChange?.({
      path: pathArray,
      title: fileName,
      content: nodeContent,
      extra: originalNode?.extra,
    });

    const isControlled = selectedFile !== undefined;
    if (!isControlled) {
      setValidSelectedFile(true);
      setSelectedFileState(pathArray);
    }

    if (originalNode) {
      onFileClick?.(keys[0], originalNode.content, originalNode.extra);
    }
  };

  const handleExpand: TreeProps["onExpand"] = (keys) => {
    const newPaths = keys as string[];

    if (!controlled) {
      setExpandedPathsState(newPaths);
    }

    onExpandedPathsChange?.(newPaths);
  };

  // ====== 创建节点核心逻辑（供命令式 API 和 moreActions 共享） ======
  const internalCreateNode = useCallback(
    (
      parentPath: string,
      options: { type: "file" | "folder"; defaultName?: string; extra?: any },
    ) => {
      const defaultName =
        options.defaultName ||
        (options.type === "folder" ? "新建文件夹" : "新建文件");
      const tempKey = `__create__${parentPath || "root"}__${Date.now()}`;

      setCreateContext({
        parentKey: parentPath,
        type: options.type,
        extra: options.extra,
      });
      setEditNode({
        key: tempKey,
        type: options.type || "folder",
        originalTitle: "",
        currentValue: defaultName,
        mode: "create",
      });

      // 自动展开父文件夹（仅在受控模式下需要，非受控模式由 defaultExpandAll 处理）
      if (!controlled && parentPath && parentPath !== "/") {
        setExpandedPathsState((prev) => {
          const current = prev || [];

          if (current.includes(parentPath)) {
            return current;
          }

          return [...current, parentPath];
        });
      }
    },
    [controlled],
  );

  // ====== 命令式 API ======
  useImperativeHandle(ref, () => ({ createNode: internalCreateNode }), [
    internalCreateNode,
  ]);

  return (
    <div
      className={clsx(
        styles.container,
        !showPreview && styles.containerNoPreview,
        className,
      )}
      style={{
        ...style,
        ...(style?.background
          ? ({
              "--folder-tree-directory-bg": style.background,
            } as React.CSSProperties)
          : {}),
        ...(style?.backgroundColor
          ? ({
              "--folder-tree-directory-bg": style.backgroundColor,
            } as React.CSSProperties)
          : {}),
      }}
    >
      <DirectoryTreeComponent
        treeData={normalizedTreeData}
        directoryIcons={directoryIcons}
        selectedKeys={
          selectable && selectedFileState && validSelectedFile
            ? [selectedFileState.join("/")]
            : []
        }
        expandedKeys={controlled ? expandedPaths : expandedPathsState}
        onSelect={handleSelect}
        onExpand={handleExpand}
        defaultExpandAll={defaultExpandAll}
        directoryTitle={directoryTitle}
        moreActions={moreActions}
        showLine={showLine}
        switcherIcon={switcherIcon}
        width={directoryTreeWidth}
        draggable={draggable}
        editNode={editNode}
        onEditValueChange={handleEditValueChange}
        onEditConfirm={handleEditConfirm}
        onEditCancel={handleEditCancel}
        createInfo={createContext}
        onStartRename={handleStartRename}
        onCreateNode={internalCreateNode}
        onDrop={onDrop}
      />
      {showPreview && (
        <FilePreview
          selectedFile={validSelectedFile ? selectedFileState : []}
          fileContent={fileContent}
          loading={loadingContent}
          previewTitle={previewTitle}
          previewRender={previewRender}
          emptyRender={emptyRender}
          getFileNode={(path) => {
            if (!path || path.length === 0) return undefined;
            const { node } = findNodeAndValidate(path);
            return node
              ? { title: node.title, path: node.path, content: node.content }
              : undefined;
          }}
        />
      )}
    </div>
  );
});

FolderTree.displayName = "FolderTree";

export default FolderTree;
